import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGlobalContext } from 'GlobalContext';
import { AttachmentType, NewAttachment } from '../../../views/attachment/AttachmentTypes';
import Dropzone from './Dropzone';
import CheckboxCheck from 'assets/CheckboxCheck.svg';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilePdf, faPaperclip, faTrash } from '@fortawesome/free-solid-svg-icons';

interface AttachmentSelectProps {
    attachments: AttachmentType[];
    addObjectPdf?: {
        shouldAddObjectPdf: boolean;
        addObjectPdfValue: boolean;
        connectedFeature: string;
    };
    acceptedFileTypes: string[];
    mode?: 'settings' | 'email_form';
    onChange: (updated: {
        attachments: AttachmentType[],
        newAttachments: NewAttachment[],
        addObjectPdfValue?: boolean
    }) => void;
};

const AttachmentSelect: React.FC<AttachmentSelectProps> = ({
    attachments, addObjectPdf, acceptedFileTypes, mode, onChange
}) => {
    const { t } = useTranslation();
    const { setFloatingAlert } = useGlobalContext();
    const [localAttachments, setLocalAttachments] = useState<AttachmentType[]>(attachments ?? []);
    const [newAttachments, setNewAttachments] = useState<NewAttachment[]>([]);
    const [localAddObjectPdf, setLocalAddObjectPdf] = useState<boolean>(addObjectPdf?.addObjectPdfValue || false);
    const didMount = React.useRef(false);

    // Set or update the current value
    useEffect(() => {
        setLocalAddObjectPdf(addObjectPdf?.addObjectPdfValue ?? false);
    }, [addObjectPdf]);

    // Call the on change whenever the local state changes
    useEffect(() => {
        if (!didMount.current) {
            didMount.current = true;
            return;
        }

        onChange({
            attachments: localAttachments,
            newAttachments: newAttachments,
            ...(addObjectPdf?.shouldAddObjectPdf ? { addObjectPdfValue: localAddObjectPdf } : {})
            
        });
    }, [localAttachments, newAttachments, localAddObjectPdf]);

    // Add a new attachment
    const handleAddNewAttachment = (file: File | null) => {
        if (!file) return;

        // Validate if the selected file is in the accepted file types
        const fileExtension = '.' + file.name.split('.').pop();
        if (!acceptedFileTypes.includes(fileExtension.toLowerCase())) {
            setFloatingAlert({ type: 'warning', message: t('validation.attachment.unsupported_file_type')});
            return;
        }

        // Update the local state
        setNewAttachments(prev => [...prev, { 
            file, 
            filename: file.name 
        }]);
    };

    // Handle the deletion of an existing attachment
    const handleDeleteExistingAttachment = (attachmentId: number | null) => {
        if (!attachments || attachmentId === null) return;
    
        // Mark the existing attachment as deleted
        const updatedAttachments = localAttachments.map(localAttachment => localAttachment.id === attachmentId 
            ? { ...localAttachment, deleted: true } 
            : localAttachment);

        // Update the local state
        setLocalAttachments(updatedAttachments);
    };

    // Handle the deletion of a new attachment
    const handleDeleteNewAttachment = (indexToRemove: number) => {
        setNewAttachments(prev => prev.filter((_, index) => index !== indexToRemove));
    };

    // Get the object pdf name
    const getObjectPdfName = () => {
        const objectNameMap: Record<string, string> = {
            jobs: t('email.email_template.connected_feature.singular.jobs'),
            job: t('email.email_template.connected_feature.singular.jobs'),
            proposals: t('email.email_template.connected_feature.singular.proposals'),
            proposal: t('email.email_template.connected_feature.singular.proposals'),
            orders: t('email.email_template.connected_feature.singular.orders'),
            order: t('email.email_template.connected_feature.singular.orders'),
            invoices: t('email.email_template.connected_feature.singular.invoices'),
            invoice: t('email.email_template.connected_feature.singular.invoices')
        };

        const objectName = addObjectPdf?.connectedFeature 
            ? objectNameMap[addObjectPdf.connectedFeature] 
            : t('general.document');

        const formattedObjectName = objectName.charAt(0).toUpperCase() + objectName.slice(1);

        return `${formattedObjectName} pdf`;
    };

    return (
        <div className='attachments-field'>
            <div className='attachments-list'>
                {localAttachments?.filter(attachment => !attachment.deleted).map((attachment, index) => (
                    <div className='attachments-row' key={index}>
                        <div className='attachment-icon'>
                            <FontAwesomeIcon icon={faPaperclip} />
                        </div>
                        <div className='attachment-name'>
                            <span className='attachment-link'>
                                {attachment.filename}
                            </span>
                        </div>
                        <div className='delete-icon tooltip-icon'>
                            <FontAwesomeIcon 
                                icon={faTrash} 
                                onClick={() => handleDeleteExistingAttachment(attachment.id)} />
                            <span className="tooltip">{t('general.delete')}</span>
                        </div>
                    </div>
                ))}
                {newAttachments?.map((attachment, index) => (
                    <div className='attachments-row small' key={index}>
                        <div className='attachment-icon'>
                            <FontAwesomeIcon icon={faPaperclip} />
                        </div>
                        <div className='attachment-name'>
                            <span className='attachment-link'>
                                {attachment.filename}
                            </span>
                        </div>
                        <div className='delete-icon tooltip-icon'>
                            <FontAwesomeIcon 
                                icon={faTrash}
                                onClick={() => handleDeleteNewAttachment(index)} />
                            <span className="tooltip">{t('general.delete')}</span>
                        </div>
                    </div>
                ))}

                {mode === 'email_form' && addObjectPdf?.shouldAddObjectPdf && localAddObjectPdf && (
                    <div className='attachments-row object-pdf-placeholder'>
                        <div className='attachment-icon'>
                            <FontAwesomeIcon icon={faFilePdf} />
                        </div>
                        <div className='attachment-name'>
                            {getObjectPdfName()}
                        </div>
                        <div className='delete-icon tooltip-icon'>
                            <FontAwesomeIcon 
                                icon={faTrash}
                                onClick={() => setLocalAddObjectPdf(false)} 
                            />
                            <span className="tooltip">{t('general.delete')}</span>
                        </div>
                    </div>
                )}



            </div>
            <Dropzone
                acceptedFileTypes={acceptedFileTypes}
                placeholder={'attachment.general.drag_drop_placeholder'}
                onFileSelect={(file) => handleAddNewAttachment(file)}
            />
            {mode === 'settings' && addObjectPdf?.shouldAddObjectPdf && (
                <div>
                    <div className='checkbox-item left'>
                        <div className="label-description-container">
                            <label htmlFor='add_object_pdf' className='checkbox-label'>
                                {t('attachment.general.add_object_pdf_label', { object: t(`email.email_template.connected_feature.singular.${addObjectPdf.connectedFeature}`) })}
                            </label>
                        </div>
                        <div className='checkbox-wrapper'>
                            <input 
                                type='checkbox'
                                id='add_object_pdf'
                                checked={localAddObjectPdf}
                                onChange={(e) => setLocalAddObjectPdf(e.target.checked)}
                                className='hidden-checkbox' />
                            <label htmlFor='add_object_pdf'
                                className={`custom-checkbox left ${localAddObjectPdf === true ? 'checked' : ''}`}>
                                {localAddObjectPdf === true && <img src={CheckboxCheck} alt="CheckboxCheck" className='checkbox-check' />}
                            </label>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

export default AttachmentSelect;